import { useGlobalToast } from 'transfix-ui/components/Toast'
import { IError, Maybe } from 'types/graphqlTypes'
import useAwardedLaneMutations from 'apps/marketplace/pages/awarded-lanes/hooks/useAwardedLaneMutations'

export const TOAST_MESSAGE = {
  unassign: {
    success: 'Successfully unassigned carrier sales rep.',
    failure: 'Failed to unassign carrier sales rep.',
  },
  reassign: {
    success: 'Successfully reassigned carrier sales rep.',
    failure: 'Failed to reassign carrier sales rep.',
  },
}

interface ICarrierSalesRepMutationResult {
  carrierSalesRepId: Maybe<string>
  errors: IError[] | undefined
}

interface IUseCarrierSalesRepProps {
  awardedLaneId?: string
}

interface IUseCarrierSalesRepState {
  reassign: (newSalesRepId: Maybe<string>) => Promise<ICarrierSalesRepMutationResult>
  loading: boolean
}

export const useCarrierSalesRep = ({
  awardedLaneId,
}: IUseCarrierSalesRepProps): IUseCarrierSalesRepState => {
  const { openToast } = useGlobalToast()

  // GraphQL Mutation
  const { loading, reassignCarrierSalesRep } = useAwardedLaneMutations({
    awardedLaneId,
  })

  const openReassignErrorToast = (reassign: boolean, message: string) => {
    openToast({
      toastType: 'alert',
      toastMessage: `${
        reassign ? TOAST_MESSAGE.reassign.failure : TOAST_MESSAGE.unassign.failure
      }\n${message}`,
    })
  }

  const openReassignSuccessToast = (reassign: boolean) => {
    openToast({
      toastType: 'default',
      toastMessage: reassign ? TOAST_MESSAGE.reassign.success : TOAST_MESSAGE.unassign.success,
    })
  }

  const reassign = async (
    newSalesRepId: Maybe<string>
  ): Promise<ICarrierSalesRepMutationResult> => {
    try {
      const result = await reassignCarrierSalesRep(newSalesRepId)
      const { awardedLaneId, errors } = result ?? {}

      if (awardedLaneId) {
        // show success toast
        openReassignSuccessToast(Boolean(newSalesRepId))
      } else if (errors) {
        // show error toast
        const errorMessages = errors.map(error => error.message).join('\n')
        openReassignErrorToast(Boolean(newSalesRepId), errorMessages)
      }
      return { carrierSalesRepId: newSalesRepId, errors }
    } catch (err) {
      // show error toast
      openReassignErrorToast(Boolean(newSalesRepId), err.message)

      return {
        carrierSalesRepId: undefined,
        errors: [{ key: 'error', message: err.message }],
      }
    }
  }

  return {
    reassign,
    loading,
  }
}
